import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Input,
  Textarea,
  Chip,
} from '@material-tailwind/react';
import {
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import Select from 'react-select';
import { DataContext } from '../../context/DataContext';

function SubjectAircraftPanel({ draft, onValuesChange, onDataChanged }) {
  // Get manufacturer/model data from context
  const dataContext = useContext(DataContext);
  const manufacturerData = dataContext.getManufacturerData();
  const modelData = dataContext.getModelData();

  // Original values from draft (for change detection)
  const originalSubjectAircraft = useMemo(() => draft?.subjectAircraft || {}, [draft?.subjectAircraft]);

  // Form state
  const [registration, setRegistration] = useState(originalSubjectAircraft.registration || '');
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [year, setYear] = useState(originalSubjectAircraft.year?.toString() || '');
  const [hours, setHours] = useState(originalSubjectAircraft.totalTime?.toString() || '');
  const [additionalDetails, setAdditionalDetails] = useState(originalSubjectAircraft.additionalDetails || '');

  // Build manufacturer options from DataContext
  const manufacturerOptions = useMemo(() => {
    if (!Object.keys(manufacturerData).length) return [];
    return Object.keys(manufacturerData)
      .sort()
      .map((k) => ({ value: manufacturerData[k], label: k }));
  }, [manufacturerData]);

  // Build model options filtered by selected manufacturer
  const modelOptions = useMemo(() => {
    if (!Object.keys(modelData).length || !selectedManufacturer) return [];

    const options = [];
    for (const [key, value] of Object.entries(modelData)) {
      const manufacturersForModel = new Set(value.manufacturers);
      const manufacturerIds = new Set(selectedManufacturer.value?.ids || []);
      const hasIntersection = manufacturersForModel.intersection
        ? manufacturersForModel.intersection(manufacturerIds).size > 0
        : [...manufacturersForModel].some(id => manufacturerIds.has(id));

      if (hasIntersection) {
        options.push({ value: value, label: key });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [modelData, selectedManufacturer]);

  // Reset form when switching to a different draft
  useEffect(() => {
    const sa = draft?.subjectAircraft || {};
    setRegistration(sa.registration || '');
    setYear(sa.year?.toString() || '');
    setHours(sa.totalTime?.toString() || '');
    setAdditionalDetails(sa.additionalDetails || '');

    // Find matching manufacturer option
    if (sa.manufacturer && Object.keys(manufacturerData).length) {
      const matchingMfr = Object.keys(manufacturerData).find(
        k => k.toLowerCase() === sa.manufacturer.toLowerCase()
      );
      if (matchingMfr) {
        setSelectedManufacturer({ value: manufacturerData[matchingMfr], label: matchingMfr });
      } else {
        // Allow custom value if not in list
        setSelectedManufacturer({ value: { ids: [] }, label: sa.manufacturer });
      }
    } else {
      setSelectedManufacturer(null);
    }

    // Find matching model option
    if (sa.model && Object.keys(modelData).length) {
      const matchingModel = Object.keys(modelData).find(
        k => k.toLowerCase() === sa.model.toLowerCase()
      );
      if (matchingModel) {
        setSelectedModel({ value: modelData[matchingModel], label: matchingModel });
      } else {
        // Allow custom value if not in list
        setSelectedModel({ value: {}, label: sa.model });
      }
    } else {
      setSelectedModel(null);
    }
  }, [draft?.draftId, draft?.subjectAircraft, manufacturerData, modelData]);

  // Detect what changed
  const dataChanged = useMemo(() => {
    const orig = originalSubjectAircraft;
    const currentManufacturer = selectedManufacturer?.label || '';
    const currentModel = selectedModel?.label || '';
    return (
      (registration || '') !== (orig.registration || '') ||
      currentManufacturer !== (orig.manufacturer || '') ||
      currentModel !== (orig.model || '') ||
      (year || '') !== (orig.year?.toString() || '') ||
      (hours || '') !== (orig.totalTime?.toString() || '') ||
      (additionalDetails || '') !== (orig.additionalDetails || '')
    );
  }, [registration, selectedManufacturer, selectedModel, year, hours, additionalDetails, originalSubjectAircraft]);

  // Notify parent of current values whenever they change
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange({
        registration,
        manufacturer: selectedManufacturer?.label || '',
        model: selectedModel?.label || '',
        year: year ? parseInt(year, 10) : undefined,
        hours: hours ? parseInt(hours, 10) : undefined,
        additionalDetails,
      });
    }
  }, [registration, selectedManufacturer, selectedModel, year, hours, additionalDetails, onValuesChange]);

  // Handle manufacturer change - clear model when manufacturer changes
  const handleManufacturerChange = (option) => {
    setSelectedManufacturer(option);
    setSelectedModel(null);
  };

  // Notify parent of change status
  useEffect(() => {
    if (onDataChanged) {
      onDataChanged(dataChanged);
    }
  }, [dataChanged, onDataChanged]);

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-blue-500" />
            <Typography variant="h6" color="blue-gray">
              Subject Aircraft
            </Typography>
          </div>
          {dataChanged && (
            <Chip
              size="sm"
              value="Modified"
              color="amber"
              variant="ghost"
            />
          )}
        </div>

        <div className="space-y-3">
          <Input
            label="Registration *"
            value={registration}
            onChange={(e) => setRegistration(e.target.value.toUpperCase())}
            placeholder="e.g., N123AB"
            size="md"
          />

          <div>
            <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
              Manufacturer
            </Typography>
            <Select
              options={manufacturerOptions}
              value={selectedManufacturer}
              onChange={handleManufacturerChange}
              placeholder="Select manufacturer..."
              isClearable
              styles={{
                control: (base) => ({ ...base, minHeight: '40px' }),
              }}
            />
          </div>

          <div>
            <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
              Model
            </Typography>
            <Select
              options={modelOptions}
              value={selectedModel}
              onChange={setSelectedModel}
              placeholder={selectedManufacturer ? "Select model..." : "Select manufacturer first"}
              isDisabled={!selectedManufacturer}
              isClearable
              styles={{
                control: (base) => ({ ...base, minHeight: '40px' }),
              }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              label="Model Year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="e.g., 2019"
              size="md"
            />

            <Input
              type="number"
              label="Total Hours"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="e.g., 850"
              size="md"
            />
          </div>

          <Textarea
            label="Additional Details (equipment, upgrades, condition)"
            value={additionalDetails}
            onChange={(e) => setAdditionalDetails(e.target.value)}
            rows={2}
          />
        </div>
      </CardBody>
    </Card>
  );
}

export default SubjectAircraftPanel;
